import { ReactElement } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ContentProgressIndicator } from 'shared/layout/ContentProgressIndicator';
import { useCarrierFeatures } from 'shared/settings/CarrierSettingsAPI';
import { isFlagEnabled } from 'shared/settings/FeatureToggles';
import { DASHBOARD_BASE_PATH, HOME_BASE_PATH } from '../data/useHomeBasePath';

interface HomeRouteGuardProps {
  /** Base path the wrapped route is mounted at. */
  basePath: string;
  children: ReactElement;
}

/**
 * The home page is mounted at both `/home` and the legacy `/dashboard`.
 * `ctms_homepage` decides which one is canonical, and the other redirects to
 * it so old `/dashboard` links keep working.
 */
export function HomeRouteGuard({ basePath, children }: HomeRouteGuardProps) {
  const features = useCarrierFeatures();
  const { pathname, search, hash } = useLocation();

  // Feature toggles read as disabled until the settings request resolves,
  // which would bounce `/home` visitors to `/dashboard` and back.
  if (!features) {
    return <ContentProgressIndicator />;
  }

  const canonicalBasePath = isFlagEnabled(features, 'ctms_homepage')
    ? HOME_BASE_PATH
    : DASHBOARD_BASE_PATH;

  if (basePath !== canonicalBasePath) {
    return (
      <Navigate
        replace={true}
        to={{
          pathname: canonicalBasePath + pathname.slice(basePath.length),
          search,
          hash,
        }}
      />
    );
  }

  return children;
}
